// frontend/src/pages/EventDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import axiosClient from '../../api/axiosClient';
import useEvent from '../../hooks/useEvent';
import { useAuth } from '../../context/AuthContext';
import MainNavbar from '../../components/Navbar/MainNavbar';
import EventHeader from '../../components/EventHeader/EventDetailHeader';
import EventDetailContent from '../../components/EventContent/EventDetailContent';
import EventSummary from '../../components/EventSummary/EventSummary';
import EventFixedAction from '../../components/EventFixedAction';
import EventEnrollFlow from '../../components/Enroll/EventEnrollFlow';
import PaidCheckoutStatusModal from '../../components/Enroll/PaidCheckoutStatusModal';
import EnrollmentStatus from '../../components/EventSummary/EnrollmentStatus';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import EventDetailMobile from './EventDetailMobile';
import { Helmet } from '../../lib/helmetCompat';
import useVisitTracking from '../../hooks/useVisitTracking';

import AllAttendeesDrawer from '../../components/Drawer/AllAttendeesDrawer';
import EnrollmentDetailsDrawer from '../../components/Drawer/EnrollmentDetailsDrawer';
import ContactOrganizerModal from '../../components/Modal/ContactOrganizerModal';
import RequestCancellationModal from '../../components/Modal/RequestCancellationModal';
import FreeCancellationModal from '../../components/Modal/FreeCancellationModal';
import { theme } from '../../theme';
import { VehicleProvider } from '../../context/VehicleContext';
import { toast } from 'react-toastify';
import { TICKET_PAID } from '../../constants/eventTypes';
import {
  buildPaidCheckoutSearch,
  clearPaidCheckoutSearch,
  readPaidCheckoutContextFromSearch
} from '../../utils/paidCheckoutParams';


const EventDetail = () => {
  const { id } = useParams();
  const { user, loading: loadingAuth } = useAuth();
  const { event, isOwner, loadingEvent, error, setEvent, fetchEvent } = useEvent(id, user);
  const navigate = useNavigate();
  const location = useLocation();
  const [showEnrollFlow, setShowEnrollFlow] = useState(false);
  const [checkoutStatusContext, setCheckoutStatusContext] = useState(null);
  const [resumePaymentSession, setResumePaymentSession] = useState(null);
  const [resumeTicketId, setResumeTicketId] = useState(null);

  const [showAllAttendeesDrawer, setShowAllAttendeesDrawer] = useState(false);
  const [showEnrollmentDetailsDrawer, setShowEnrollmentDetailsDrawer] = useState(false);
  const [showContactOrganizerModal, setShowContactOrganizerModal] = useState(false);
  const [showRequestCancellationModal, setShowRequestCancellationModal] = useState(false);
  const [showFreeCancellationModal, setShowFreeCancellationModal] = useState(false);
  const [isRequestingCancellation, setIsRequestingCancellation] = useState(false);
  const [isCancellingFree, setIsCancellingFree] = useState(false);

  useVisitTracking({ eventId: id, isOwner, source: 'event_detail' })

  const now = new Date()
  const eventEnd = event ? new Date(event.endDate) : null
  const eventStart = event ? new Date(event.startDate) : null
  const hasPassed = eventEnd && now > eventEnd
  const hasStarted = eventStart && now > eventStart

  // Hook para dimensiones de pantalla
  const { width } = useWindowDimensions();
  const isMobile = width < parseInt(theme.breakpoints.md);
  // const isTablet = width < parseInt(theme.breakpoints.lg); // Ajustar breakpoint según sea necesari

  // Escuchar eventos personalizados para forzar actualizaciones
  useEffect(() => {
    const handleEnrollmentUpdate = async () => {
      console.log('🔄 Evento enrollment-updated recibido, refrescando...');
      await fetchEvent();
      console.log('✅ Evento actualizado por evento personalizado');
    };

    window.addEventListener('enrollment-updated', handleEnrollmentUpdate);
    return () => {
      window.removeEventListener('enrollment-updated', handleEnrollmentUpdate);
    };
  }, [fetchEvent]);

  // Detectar parámetro openContactModal en la URL y abrir el modal automáticamente
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const shouldOpenContactModal = urlParams.get('openContactModal');

    if (shouldOpenContactModal === 'true' && user && !loadingAuth) {
      setShowContactOrganizerModal(true);
      // Limpiar el parámetro de la URL sin recargar la página
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('openContactModal');
      window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
    }
  }, [location.search, user, loadingAuth]);

  useEffect(() => {
    // Don't open the status modal while the user is actively in the payment
    // flow — URL params added by handlePaymentSessionCreated would otherwise
    // trigger it mid-checkout.
    if (showEnrollFlow) {
      return;
    }

    const checkoutContextFromUrl = readPaidCheckoutContextFromSearch(location.search);

    if (!checkoutContextFromUrl) {
      return;
    }

    setCheckoutStatusContext((currentContext) => {
      if (
        currentContext &&
        currentContext.paymentId === checkoutContextFromUrl.paymentId &&
        currentContext.reservationId === checkoutContextFromUrl.reservationId
      ) {
        // URL never stores paymentIntentId — don't overwrite an in-memory
        // context (which may have it) with one that has null.
        // If the URL has a paymentIntentId (Stripe redirect), pick it up.
        if (
          checkoutContextFromUrl.paymentIntentId &&
          checkoutContextFromUrl.paymentIntentId !== currentContext.paymentIntentId
        ) {
          return { ...currentContext, paymentIntentId: checkoutContextFromUrl.paymentIntentId };
        }
        return currentContext;
      }

      return checkoutContextFromUrl;
    });
  }, [location.search, showEnrollFlow]);

  const replaceCheckoutSearch = (nextSearch) => {
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch
      },
      { replace: true }
    );
  };

  const persistCheckoutContext = (checkoutContext) => {
    if (!checkoutContext?.paymentId || !checkoutContext?.reservationId) {
      return;
    }

    replaceCheckoutSearch(buildPaidCheckoutSearch({
      currentSearch: location.search,
      paymentId: checkoutContext.paymentId,
      reservationId: checkoutContext.reservationId
    }));
  };

  const clearPersistedCheckout = () => {
    setCheckoutStatusContext(null);
    setResumePaymentSession(null);
    setResumeTicketId(null);
    replaceCheckoutSearch(clearPaidCheckoutSearch(location.search));
  };

  if (loadingAuth || (loadingEvent && !event)) return <p>Cargando...</p>;
  if (error) return <p>Error al cargar: {error}</p>;
  if (!event) return <p>Evento no encontrado</p>;

  // Función para inscribir al usuario; se espera que el endpoint devuelva el evento actualizado
  const enrollUser = async (ticket, vehicle, paymentContext = null) => {
    try {
      const response = await axiosClient.post(`/api/events/enroll/${event.id}`, {
        ticketId: ticket._id,
        vehicleId: vehicle?._id || null,
        paymentId: paymentContext?.paymentId || null,
        reservationId: paymentContext?.reservationId || null,
        paymentIntentId: paymentContext?.paymentIntentId || null,
      });
      if (response.data.success) {
        console.log(
          `[ENROLL-FRONT] ${new Date().toISOString()} — Usuario ${user.id}` +
          ` inscribiéndose en evento ${event.id}` +
          ` con ticket ${ticket._id}` +
          (vehicle?._id ? ` y vehículo ${vehicle._id}` : '') +
          (paymentContext?.paymentId ? ` usando payment ${paymentContext.paymentId}` : '')
        );

        // Actualizamos el evento en el estado global (y por lo tanto en el contexto)
        setEvent(response.data.event);
        return response.data.event;
      } else {
        throw new Error(response.data.message || 'Error en la inscripción.');
      }
    } catch (err) {
      console.error('Error en enrollUser:', err);
      throw err;
    }
  };

  // Calculamos si ya estamos inscritos y en estado activo
  const isEnrolled = user && Array.isArray(event.attendees) && event.attendees.some(att =>
    // mismo userId y estado activo (no cancelado)
    ((att.userId._id || att.userId.id).toString() === user.id.toString())
    && !['not_attending', 'cancelled_by_organizer', 'refunded', 'rejected_by_organizer', 'cancelled_by_user'].includes(att.status)
  );

  // Extraemos el objeto attendee sólo si estamos inscritos activamente
  const attendee = isEnrolled
    ? event.attendees.find(att =>
      (att.userId._id || att.userId.id).toString() === user.id.toString()
      && !['not_attending', 'cancelled_by_organizer', 'refunded', 'rejected_by_organizer', 'cancelled_by_user'].includes(att.status)
    )
    : null;


  // Formatear el objeto date para pasarlo a los componentes hijos
  const formattedDate = {
    startDate: event.startDate,
    endDate: event.endDate,
  };

  const mapCoords = {
    lat:
      event.locationCoordinates && event.locationCoordinates.coordinates
        ? event.locationCoordinates.coordinates[1]
        : undefined,
    lng:
      event.locationCoordinates && event.locationCoordinates.coordinates
        ? event.locationCoordinates.coordinates[0]
        : undefined,
  };

  // ¿Había inscrito antes y canceló o fue cancelado?
  const hadPriorEnrollment = user && Array.isArray(event.attendees) && event.attendees.some(att =>
    (att.userId._id || att.userId.id).toString() === user.id.toString()
    && (att.status === 'not_attending' || att.status === 'cancelled_by_organizer' || att.status === 'refunded' || att.status === 'rejected_by_organizer')
  );

  // Recuperar el registro anterior más reciente
  const priorAttendee = hadPriorEnrollment
    ? event.attendees
      .filter(att =>
        (att.userId._id || att.userId.id).toString() === user.id.toString()
        && (att.status === 'not_attending' || att.status === 'cancelled_by_organizer' || att.status === 'refunded' || att.status === 'rejected_by_organizer')
      )
      .sort((a, b) => new Date(b.createdAt || b.enrolledAt) - new Date(a.createdAt || a.enrolledAt))[0] || null
    : null;

  const handleEnrollClick = () => {
    if (hasPassed) return
    if (hasStarted) return
    if (event.status === 'cancelled') return
    if (!user) {
      const returnTo = location.pathname
      navigate('/signin', { state: { returnTo } })
    } else {
      setShowEnrollFlow(true);
    }
  }

  const attendeeId = attendee?._id;
  const ticket = attendee
    ? event.tickets.find(
      t => t._id.toString() === attendee.ticketId._id?.toString() || t._id.toString() === attendee.ticketId.toString()
    )
    : null;
  const resumeTicket = resumeTicketId
    ? event.tickets.find((eventTicket) => eventTicket._id.toString() === resumeTicketId.toString()) || null
    : null;
  const status = attendee?.status;





  const cancelFree = async (userMessage = '') => {
    setIsCancellingFree(true);
    try {
      await axiosClient.post(`/api/events/${event.id}/attendees/cancel`, {
        userMessage: userMessage.trim()
      });
      await fetchEvent();
      toast.success('Inscripción cancelada.');
      setShowFreeCancellationModal(false);
    } catch (err) {
      console.error('Error en cancelFree:', err);
      toast.error(err.response?.data?.message || 'Error al cancelar.');
    } finally {
      setIsCancellingFree(false);
    }
  };

  const requestPaidCancel = async (userMessage = '') => {
    setIsRequestingCancellation(true);
    try {
      await axiosClient.post(
        `/api/events/${event.id}/attendees/${attendeeId}/request-cancel`,
        { userMessage }
      );
      toast.info('Solicitud de cancelación enviada.');
      setShowRequestCancellationModal(false);
      // Actualizar los datos del evento para reflejar el cambio de estado
      await fetchEvent();
      // Redirigir a los detalles del evento
      navigate(`/events/${event.id}/${event.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error solicitando cancelación.');
    } finally {
      setIsRequestingCancellation(false);
    }
  };

  const handleCancelEnrollment = () => {
    if (!ticket) return;
    if (ticket.type === TICKET_PAID) {
      setShowRequestCancellationModal(true);
    } else if (ticket.approvalRequired && status === 'confirmation_pending') {
      toast.error('No puedes cancelar mientras tu inscripción esté pendiente.');
    } else {
      setShowFreeCancellationModal(true);
    }
  };

  const handleContactOrganizer = () => {
    setShowContactOrganizerModal(true);
  };

  const handleEnrollmentDetailsDrawerOpen = () => {
    setShowEnrollmentDetailsDrawer(true);
  };

  const handleEnrollmentDetailsDrawerClose = () => {
    setShowEnrollmentDetailsDrawer(false);
  };

  const handlePaymentSessionCreated = (paymentSession) => {
    persistCheckoutContext(paymentSession);
  };

  const handlePaidCheckoutStateChange = (paymentSession) => {
    persistCheckoutContext(paymentSession);
    setResumePaymentSession(null);
    setResumeTicketId(null);
    setCheckoutStatusContext(paymentSession);
    setShowEnrollFlow(false);
  };

  const handleRetryPaidCheckout = (checkout) => {
    setResumePaymentSession({
      clientSecret: checkout.clientSecret,
      connectedAccountId: checkout.connectedAccountId,
      paymentId: checkout.paymentId,
      reservationId: checkout.reservationId,
    });
    setResumeTicketId(checkout.ticketId || null);
    setCheckoutStatusContext(null);
    setShowEnrollFlow(true);
    // Remove Stripe redirect params left over from a previous redirect so they
    // don't leak into the next payment attempt's return_url or URL effect.
    replaceCheckoutSearch(buildPaidCheckoutSearch({
      currentSearch: location.search,
      paymentId: checkout.paymentId,
      reservationId: checkout.reservationId,
    }));
  };

  const handleCheckoutConfirmed = async () => {
    await fetchEvent();
  };

  const handleViewEnrollmentFromCheckout = async () => {
    await fetchEvent();
    setShowEnrollmentDetailsDrawer(true);
  };

  // Construir la URL del evento
  const origin = process.env.REACT_APP_CLIENT_URL || (typeof window !== 'undefined' ? window.location.origin : '')
  const eventLink = `${origin}/events/${event.id}/${event.slug}`
  const absoluteEventImage = (() => {
    if (!event.image) return 'https://motorik.net/images/motorik-og-image.jpg'
    if (/^https?:\/\//.test(event.image)) return event.image
    try {
      return new URL(event.image, process.env.REACT_APP_API_URL || origin).toString()
    } catch {
      return 'https://motorik.net/images/motorik-og-image.jpg'
    }
  })()

  return (
    <EventDetailWrapper>
      <Helmet>
        <title>{`${event.title} - Motorik`}</title>
        <meta name="description" content={event.description || 'Evento en Motorik'} />
        <link rel="canonical" href={eventLink} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Motorik" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:title" content={`${event.title} - Motorik`} />
        <meta property="og:description" content={event.description || 'Evento en Motorik'} />
        <meta property="og:image" content={absoluteEventImage} />
        <meta property="og:url" content={eventLink} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={eventLink} />
        <meta name="twitter:title" content={`${event.title} - Motorik`} />
        <meta name="twitter:description" content={event.description || 'Evento en Motorik'} />
        <meta name="twitter:image" content={absoluteEventImage} />
      </Helmet>
      <MainNavbar />
      <EventHeader
        title={event.title}
        availableSeats={event.availableSeats}
        eventType={event.eventType}
        terrain={event.terrain}
        manageLink={`/events/manage/${event.id}`}
        isEnrolled={isEnrolled}
        handleEnroll={handleEnrollClick}
        disabled={hasPassed || hasStarted || event.status === 'cancelled'}
        eventId={event.id}
        organizer={event.owner}
        isOwner={isOwner}
        copyUrl={eventLink}
        eventHasPassed={hasPassed}
        onViewEnrollment={handleEnrollmentDetailsDrawerOpen}
        status={event.status}
        endDate={event.endDate}
      />

      {((isEnrolled && ticket) || (priorAttendee && ['refunded', 'cancelled_by_organizer', 'rejected_by_organizer'].includes(priorAttendee.status))) && (
        <EnrollmentStatusSection>
          <EnrollmentStatusContainer>
            <EnrollmentStatus
              enrollmentStatus={isEnrolled ? status : priorAttendee?.status}
              ticketType={isEnrolled ? ticket.type : event.tickets.find(t => t._id.toString() === (priorAttendee.ticketId._id || priorAttendee.ticketId).toString())?.type}
              approvalRequired={isEnrolled ? ticket.approvalRequired : event.tickets.find(t => t._id.toString() === (priorAttendee.ticketId._id || priorAttendee.ticketId).toString())?.approvalRequired}
              handleCancelEnrollment={handleCancelEnrollment}
              cancellationDenied={isEnrolled ? attendee?.cancellationDenied : priorAttendee?.cancellationDenied}
              handleContactOrganizer={handleContactOrganizer}
              organizerMessage={isEnrolled ? attendee?.organizerMessage : priorAttendee?.organizerMessage}
              onViewDetails={handleEnrollmentDetailsDrawerOpen}
            />
          </EnrollmentStatusContainer>
        </EnrollmentStatusSection>
      )}


      <EventBody>
        {isMobile ? (
          <EventDetailMobile
            event={{ ...event, date: formattedDate, mapCoordinates: mapCoords }}
            organizer={event.owner}
            isEnrolled={isEnrolled}
            status={status}
            ticket={ticket}
            attendee={attendee}
            priorAttendee={priorAttendee}
            handleCancelEnrollment={handleCancelEnrollment}
            handleContactOrganizer={handleContactOrganizer}
            onShowAllAttendees={() => setShowAllAttendeesDrawer(true)}
            isLoggedIn={!!user}
            onViewDetails={handleEnrollmentDetailsDrawerOpen}
            hasPassed={hasPassed}
            hasStarted={hasStarted}
            allowedVehicleTypes={event.allowedVehicleTypes}
            needsVehicle={event.needsVehicle}
          />
        ) : (
          <GridContainer>
            <EventDetailContent
              image={event.image}
              description={event.description}
              organizer={event.owner}
              eventId={event._id}
              tickets={event.tickets}
              allowedVehicleTypes={event.allowedVehicleTypes}
              needsVehicle={event.needsVehicle}
            />
            <EventSummaryContainer>
              <EventSummary
                date={formattedDate}
                location={event.location}
                shortLocation={event.shortLocation}
                mapCoordinates={mapCoords}
                attendees={event.attendees}
                attendeesCount={event.attendeesCount}
                organizer={event.owner}
                organizerVehicle={event.organizerVehicle}
                onShowAllAttendees={() => setShowAllAttendeesDrawer(true)}
                isLoggedIn={!!user}
                eventEndDate={event.endDate}
              />
            </EventSummaryContainer>
          </GridContainer>
        )}
      </EventBody>
      <EventFixedAction
        eventName={event.title}
        eventStartDate={event.startDate}
        availableSeats={event.availableSeats}
        tickets={event.tickets}
        isEnrolled={isEnrolled}
        isOwner={isOwner}
        handleEnroll={handleEnrollClick}
        onViewEnrollment={handleEnrollmentDetailsDrawerOpen}
        disabled={hasPassed || hasStarted || event.status === 'cancelled'}
        eventEndDate={event.endDate}
        attendeesCount={event.attendeesCount}
        eventId={event.id}
      />

      {showEnrollFlow && (
        <VehicleProvider>
          <EventEnrollFlow
            event={event}
            enrollUser={enrollUser}
            initialSelectedTicket={resumeTicket}
            initialPaymentSession={resumePaymentSession}
            onEnrollComplete={(updatedEvent) => {
              setShowEnrollFlow(false);
              setResumePaymentSession(null);
              setResumeTicketId(null);

              // Usar el evento actualizado directamente en lugar de hacer una nueva petición
              if (updatedEvent) {
                setEvent(updatedEvent);
              } else {
                // Fallback: refrescar los datos del evento si no se recibe el evento actualizado
                fetchEvent();
              }
            }}
            onPaymentSessionCreated={handlePaymentSessionCreated}
            onPaidCheckoutStateChange={handlePaidCheckoutStateChange}
            onCancel={() => {
              setShowEnrollFlow(false);
              setResumePaymentSession(null);
              setResumeTicketId(null);
            }}
            onViewDetails={handleEnrollmentDetailsDrawerOpen}
          />
        </VehicleProvider>

      )}
      {checkoutStatusContext && (
        <PaidCheckoutStatusModal
          isOpen={Boolean(checkoutStatusContext)}
          checkoutContext={checkoutStatusContext}
          event={event}
          onClose={() => setCheckoutStatusContext(null)}
          onClearCheckout={clearPersistedCheckout}
          onRetryPayment={handleRetryPaidCheckout}
          onViewEnrollment={handleViewEnrollmentFromCheckout}
          onCheckoutConfirmed={handleCheckoutConfirmed}
        />
      )}
      <AllAttendeesDrawer
        isOpen={showAllAttendeesDrawer}
        onClose={() => setShowAllAttendeesDrawer(false)}
        attendees={event.attendees}
        organizer={event.owner}
        organizerVehicle={event.organizerVehicle}
        attendeesCount={event.attendeesCount}
        width="400px"
      />
      <EnrollmentDetailsDrawer
        isOpen={showEnrollmentDetailsDrawer}
        onClose={handleEnrollmentDetailsDrawerClose}
        enrollment={isEnrolled ? attendee : priorAttendee}
        event={event}
        onRefetch={fetchEvent}
        width="500px"
      />
      {showContactOrganizerModal && (
        <ContactOrganizerModal
          isOpen={showContactOrganizerModal}
          onClose={() => setShowContactOrganizerModal(false)}
          eventId={event.id}
          organizerName={event.owner.name}
        />
      )}
      {showRequestCancellationModal && (
        <RequestCancellationModal
          isOpen={showRequestCancellationModal}
          onClose={() => setShowRequestCancellationModal(false)}
          onConfirm={requestPaidCancel}
          isLoading={isRequestingCancellation}
        />
      )}
      {showFreeCancellationModal && (
        <FreeCancellationModal
          isOpen={showFreeCancellationModal}
          onClose={() => setShowFreeCancellationModal(false)}
          onConfirm={cancelFree}
          isLoading={isCancellingFree}
        />
      )}
    </EventDetailWrapper>
  );
};

export default EventDetail;

// Styled Components
const EventDetailWrapper = styled.div`
  height: 100vh;
  height: 100dvh;
`;

const EventBody = styled.section`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 120px;
`;

const GridContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.md} ${({ theme }) => theme.sizing.md} 0px ${({ theme }) => theme.sizing.md};
`;

const EventSummaryContainer = styled.div`
  grid-area: 1 / 8 / 2 / 13;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const EnrollmentStatusSection = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const EnrollmentStatusContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  display: flex;
  padding: ${({ theme }) => theme.sizing.md};
  padding-bottom: 0px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0px;
  }
`;
